import {
	businessLogicValue,
	ElementSchema,
	ElementType,
	formBusinessLogicValue,
	FormElementType,
	formLogicType,
	FormNumberFormat,
	FormSearch,
	logicType,
	Search,
	Validation,
} from "schemas/elements";
import _ from "lodash";
import elementSchema from "schemas";
import { v4 as uuidv4 } from "uuid";
import { formatFilter, reformatFilter, isFilterValid } from "formatter/filter";
import { status } from "../constants";
import { State } from "hooks/FormDesigner/useElementList";
import { propertyType } from "hooks/FormDesigner/useElementList";
import api from "lib/api";
import { AxiosError } from "axios";
import { exportFile } from "utils/exportFile";
import { AlertProps, SearchSelectOptionProps } from "@alm/flow-ds/components";

export type FormLayout = {
	fullWidth: boolean;
	maxWidth: number | null;
};

export type FormJSON = {
	view: {
		id: string;
		elem: string;
		resource: string | null;
		name: string;
		elements: FormElementType[];
		formLayout: FormLayout;
		selectFieldsBasedOnForm: boolean;
		additionalSelect?: string[];
	};
	format: number;
	formLabel: string;
	onLoad?: formBusinessLogicValue;
	beforeSave?: formBusinessLogicValue;
	usePatch: boolean;
};

export type ExportFormProps = {
	elements: ElementType[];
	formId: string;
	formEntity: string | null;
	formLabel: string;
	formLayout: FormLayout;
	selectFieldsBasedOnForm: boolean;
	additionalSelect?: SearchSelectOptionProps[];
	updateValidation: (data: boolean) => void;
	addAlert: (alert: string, status: AlertProps["status"]) => void;
	addErrorAlert: (response: AxiosError, error: string) => void;
	isSave?: boolean;
	saveAs?: string;
	onLoad?: businessLogicValue;
	beforeSave?: businessLogicValue;
	usePatch: boolean;
	beforeSaveValidation: boolean;
	onLoadValidation: boolean;
	saveAsCb?: (saveAs: string) => void;
};

type FormDefinitionElementType = Omit<
	FormElementType,
	"elements" | "search"
> & {
	uuid?: string;
	elements?: FormDefinitionElementType[];
	search?: Omit<FormSearch, "view"> & {
		view?: {
			elements: FormDefinitionElementType[];
		};
	};
};

export type FormDefinition = {
	view: {
		id: string;
		elem: string;
		resource: string;
		name: string;
		elements: FormDefinitionElementType[];
		uuid?: string;
		required?: boolean;
		disabled?: boolean;
	};
	format: number;
	formLabel: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	"@namespaces": any;
	id: string;
	version: number;
	"@controls": {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		[key: string]: any;
	};
};

export default async ({
	elements,
	formId,
	formEntity,
	formLabel,
	formLayout,
	selectFieldsBasedOnForm,
	additionalSelect,
	updateValidation,
	addAlert,
	addErrorAlert,
	isSave = false,
	saveAs,
	onLoad,
	beforeSave,
	usePatch,
	beforeSaveValidation,
	onLoadValidation,
	saveAsCb,
}: ExportFormProps) => {
	if (formLabel === "" || beforeSaveValidation || onLoadValidation) {
		updateValidation(true);
		addErrorAlert({} as AxiosError, "missing");
	} else {
		const updatedElements = removeFields(elements);

		if (updatedElements === undefined) {
			updateValidation(true);
			addErrorAlert({} as AxiosError, "missing");
		} else {
			const generatedJson = generateJson(
				updatedElements,
				formId,
				formEntity,
				formLabel,
				formLayout,
				selectFieldsBasedOnForm,
				usePatch,
				onLoad,
				beforeSave,
				additionalSelect
			);
			let valid = false;
			try {
				valid = await api.definitions.runFormValidation(generatedJson);
			} catch (error) {
				const e = error as AxiosError;
				e.response?.status === 504
					? addErrorAlert(e, "errorValidatingForm")
					: addErrorAlert(e, "incorrect");
			}
			if (valid) {
				if (isSave) {
					// Check whether the form save was successfull
					// so that the right alert is displayed
					try {
						const formSuccess = await api.definitions.uploadForm(
							generatedJson,
							saveAs ? saveAs : formId
						);
						if (formSuccess === true) {
							addAlert("formSaved", status.SUCCESS);
							if (saveAs && saveAsCb) saveAsCb(saveAs);
						} else {
							addErrorAlert({} as AxiosError, "formSaveFailed");
						}
					} catch {
						addErrorAlert({} as AxiosError, "formSaveFailed");
					}
				} else {
					const json = JSON.stringify(generatedJson, undefined, 4);
					exportFile(formId, json, "json");
					updateValidation(false);
				}
			}
		}
	}
};

/**
 * If first character is not a forward slash then add one
 * @param value String to convert
 */
export const addSlash = (value: string) => {
	if (value.charAt(0) !== "/") return `/${value}`;
	else return value;
};

/**
 * Converts search property from UI to be ready for download
 *
 * @param search Search property to convert
 */
const createSearch = (search: Search | undefined) => {
	const newColumns: { id: string; label?: string }[] = [];
	if (search !== undefined && search.columns !== undefined)
		search.columns.forEach(column => {
			if (column.id !== undefined && column.id !== null)
				newColumns.push({
					id: addSlash(column.id.value),
					label: column.label === "" ? undefined : column.label,
				});
		});
	let searchElements: FormElementType[] | undefined = [];
	if (
		search !== undefined &&
		search.view !== undefined &&
		search.view.length > 0
	) {
		searchElements = removeFields(search.view);
		if (searchElements === undefined) return undefined;
	}
	const newSearch =
		search !== undefined
			? {
					...search,
					label:
						search.label !== undefined && search.label !== null
							? addSlash(search.label.value)
							: undefined,
					overwriteDataWith:
						search.overwriteDataWith !== undefined &&
						search.overwriteDataWith !== null
							? addSlash(search.overwriteDataWith.value)
							: undefined,
					columns: newColumns,
					view:
						searchElements !== undefined &&
						searchElements.length > 0
							? {
									elements: searchElements,
							  }
							: undefined,
					payload: {
						...search.payload,
						"request-uri": search.payload?.["request-uri"]?.value,
						filter: {
							AND: reformatFilter(search.payload.filter),
						},
					},
			  }
			: undefined;
	if (newSearch !== undefined && newSearch.view === undefined)
		delete newSearch.view;
	if (newSearch !== undefined && newSearch.label === undefined)
		delete newSearch.label;
	if (newSearch !== undefined && newSearch.overwriteDataWith === undefined)
		delete newSearch.overwriteDataWith;

	return newSearch;
};

export const removeFields = (
	elements: ElementType[]
): FormElementType[] | undefined => {
	const updatedElements: FormElementType[] = [];
	let fieldsMissing = false;
	elements.some(element => {
		const {
			jsonRequired,
			elementId,
			formValidation,
			elementValidation,
			id,
			optionsId,
			optionsPath,
			optionsFilter,
			searchField,
			populatesFrom,
			populatesKey,
			resource,
			search,
			validation,
			numberFormat,
			link,
			updateLink,
			createLink,
			afterPatch,
			elementLayout,
			...rest
		} = element;
		if (formValidation) {
			fieldsMissing = true;
			return true;
		} else {
			let newElements: FormElementType[] | undefined = undefined;
			if (rest.elements !== undefined && rest.elements.length > 0) {
				newElements = removeFields(rest.elements);
				if (newElements === undefined) {
					fieldsMissing = true;
					return true;
				}
			}
			const elementToUpdate: FormElementType = {
				...(rest as FormElementType),
			};

			if (newElements !== undefined)
				elementToUpdate.elements = newElements;

			if (
				rest.elem === "legacyArrayView" &&
				searchField !== undefined &&
				searchField !== null
			) {
				elementToUpdate.searchField = searchField.value;

				const newSearch = createSearch(search);

				if (newSearch !== undefined) elementToUpdate.search = newSearch;
				else {
					fieldsMissing = true;
					return true;
				}
			} else if (rest.elem !== "legacyArrayView") {
				const newSearch = createSearch(search);
				if (newSearch !== undefined) elementToUpdate.search = newSearch;
			}

			const newValidation: Validation = {};
			if (validation?.minLength !== undefined)
				newValidation.minLength = validation.minLength;
			if (validation?.maxLength !== undefined)
				newValidation.maxLength = validation.maxLength;
			if (
				validation?.format !== undefined &&
				validation?.format?.trim() !== ""
			)
				newValidation.format = validation.format;
			if (validation?.allowNull !== undefined)
				newValidation.allowNull = validation.allowNull;

			const newNumberFormat: FormNumberFormat = {};
			if (numberFormat?.precision !== undefined)
				newNumberFormat.precision = numberFormat.precision;
			if (
				numberFormat?.rounding !== undefined &&
				numberFormat?.rounding !== null
			)
				newNumberFormat.rounding = numberFormat.rounding.value;

			if (id !== undefined)
				elementToUpdate.id =
					rest.elem === "nestedView" ||
					rest.elem === "multiReferenceView"
						? typeof id === "string"
							? id
							: id.value
						: addSlash(typeof id === "string" ? id : id.value);
			if (optionsId !== undefined && optionsId !== null)
				elementToUpdate.optionsId = optionsId.value;
			if (optionsPath !== undefined && optionsPath !== null)
				elementToUpdate.optionsPath = optionsPath.value;
			if (
				optionsFilter !== undefined &&
				optionsFilter !== null &&
				optionsFilter.length > 0
			) {
				const newOptionsFilter: string[] = [];
				optionsFilter.forEach(value => {
					newOptionsFilter.push(value.value);
				});
				elementToUpdate.optionsFilter = newOptionsFilter;
			}
			if (populatesFrom !== undefined && populatesFrom !== null)
				elementToUpdate.populatesFrom = populatesFrom.value;
			if (populatesKey !== undefined && populatesKey !== null)
				elementToUpdate.populatesKey = addSlash(populatesKey.value);
			if (resource !== undefined)
				elementToUpdate.resource = resource.value;
			if (updateLink !== undefined)
				elementToUpdate.updateLink = updateLink?.value;
			if (createLink !== undefined)
				elementToUpdate.createLink = createLink?.value;
			if (!_.isEmpty(newValidation))
				elementToUpdate.validation = newValidation;
			if (!_.isEmpty(newNumberFormat))
				elementToUpdate.numberFormat = newNumberFormat;
			if (afterPatch?.logic?.length) {
				elementToUpdate.afterPatch = {
					...afterPatch,
					logic: removeEmptyParams(afterPatch.logic),
				};
			}
			if (elementLayout !== undefined) {
				elementToUpdate.elementLayout = {
					...elementLayout,
					lineBreakAfterElement:
						elementLayout.lineBreakAfterElement || false,
				};
			}
			updatedElements.push(elementToUpdate);

			return false;
		}
	});
	if (fieldsMissing) return undefined;
	return updatedElements;
};

export const generateJson = (
	elements: FormElementType[],
	formId: string,
	formEntity: string | null,
	formLabel: string,
	formLayout: FormLayout,
	selectFieldsBasedOnForm: boolean,
	usePatch: boolean,
	onLoad?: businessLogicValue,
	beforeSave?: businessLogicValue,
	additionalSelect?: SearchSelectOptionProps[]
) => {
	const TENANT: string =
		window.location.hostname.substring(
			0,
			window.location.hostname.indexOf(".")
		) || "";

	const updatedData: FormJSON = {
		view: {
			id: `forms:/${TENANT}/definitions/${formId}`,
			elem: "rootView",
			resource: formEntity,
			name: "rootView0",
			elements,
			formLayout,
			selectFieldsBasedOnForm,
			additionalSelect: additionalSelect?.map(value => {
				return addSlash(value.value);
			}),
		},
		format: 4,
		formLabel,
		...(onLoad?.logic?.length && {
			onLoad: { ...onLoad, logic: removeEmptyParams(onLoad.logic) },
		}),
		...(beforeSave?.logic?.length && {
			beforeSave: {
				...beforeSave,
				logic: removeEmptyParams(beforeSave.logic),
			},
		}),
		usePatch,
	};
	return updatedData;
};

export const removeServiceData = (formDefinition: FormDefinition) => {
	const {
		"@namespaces": namespaces,
		id,
		version,
		"@controls": controls,
		...rest
	} = formDefinition;
	delete rest.view.uuid;
	delete rest.view.required;
	delete rest.view.disabled;
	removeUUID(rest.view.elements);
	return rest;
};

export const removeUUID = (formElements: FormDefinitionElementType[]) => {
	formElements.forEach(element => {
		delete element.uuid;
		if (element.required === false) delete element.required;
		if (element.disabled === false) delete element.disabled;
		if (element.elements !== undefined && element.elements.length > 0) {
			element.elements = removeUUID(element.elements);
		}
		if (
			element.search !== undefined &&
			element.search.view !== undefined &&
			element.search.view.elements.length > 0
		) {
			element.search.view.elements = removeUUID(
				element.search.view.elements
			);
		}
	});
	return formElements;
};

export const convertForm = (
	form: FormJSON,
	addAlert: (alert: string, status: AlertProps["status"]) => void,
	addErrorAlert: ExportFormProps["addErrorAlert"]
) => {
	if (form.format === 2) {
		addErrorAlert({} as AxiosError, "formatDeprecated");
	}
	const converted: State = {
		formId: form.view.id.substring(form.view.id.lastIndexOf("/") + 1),
		formEntity: {
			value: form.view.resource,
			label:
				form.view.resource === null
					? "NoEntity"
					: form.view.resource.substring(
							form.view.resource.lastIndexOf("/") + 1
					  ),
			data: [],
		},
		formLayout: form.view.formLayout,
		selectFieldsBasedOnForm: form.view.selectFieldsBasedOnForm,
		additionalSelect:
			form.view.additionalSelect !== undefined &&
			form.view.additionalSelect.length > 0 &&
			typeof form.view.additionalSelect[0] === "string"
				? form.view.additionalSelect.map(value => {
						return {
							value: value,
							label: value.slice(value.lastIndexOf("/") + 1),
						};
				  })
				: undefined,
		validation: false,
		formLabel: form.formLabel,
		elements: addElementData(form.view.elements, addAlert),
		loading: false,
		onLoadValidation: false,
		beforeSaveValidation: false,
		onLoad:
			form.onLoad !== undefined
				? {
						...form.onLoad,
						logic: addEmptyParams(form.onLoad.logic),
				  }
				: undefined,
		beforeSave:
			form.beforeSave !== undefined
				? {
						...form.beforeSave,
						logic: addEmptyParams(form.beforeSave.logic),
				  }
				: undefined,
		usePatch: form.usePatch,
	};
	if (converted.beforeSave === undefined) delete converted.beforeSave;
	if (converted.onLoad === undefined) delete converted.onLoad;

	return converted;
};

/** Adds empty object to the `params` property in each logic if params are not present
 *
 * @param logic Array of business logic to update
 */
export const addEmptyParams = (logic: logicType[] | formLogicType[]) => {
	const newLogic: logicType[] = [];
	logic.forEach(logic => {
		newLogic.push({
			...logic,
			params:
				logic.params !== undefined && logic.params !== null
					? logic.params
					: {},
		});
	});
	return newLogic;
};

/** Removes any empty object from the `params` property in each logic if there is one
 *
 * @param logic Array of business logic to update
 */
export const removeEmptyParams = (logic: logicType[]) => {
	const newLogic: formLogicType[] = [];
	logic.forEach(logic => {
		newLogic.push({
			...logic,
			params:
				logic.params !== undefined &&
				logic.params !== null &&
				!_.isEmpty(logic.params)
					? logic.params
					: undefined,
		});
	});
	return newLogic;
};

const addElementData = (
	elements: (FormElementType | ElementType)[],
	addAlert: (alert: string, status: AlertProps["status"]) => void
) => {
	const elementsArray: ElementType[] = [];
	elements.forEach(element => {
		const jsonRequired: propertyType[] = [];
		const thisElement = (elementSchema as ElementSchema)[element.elem];
		const booleanData: {
			allowCreation?: boolean;
			isArray?: boolean;
			loadCollapsed?: boolean;
			allowEmptyStringAsNull?: boolean;
		} = {};
		for (const property of Object.keys(thisElement)) {
			if (property === "allowCreation") {
				booleanData[property] = false;
			}
			if (property === "isArray") {
				booleanData[property] = false;
			}
			if (property === "loadCollapsed") {
				booleanData[property] = false;
			}
			if (property === "allowEmptyStringAsNull") {
				booleanData[property] = false;
			}
			if (
				property !== "elem" &&
				thisElement[property as propertyType]?.required
			) {
				jsonRequired.push(property as propertyType);
			}
		}

		const updatedOptionsFilter: SearchSelectOptionProps[] = [];

		if (element.optionsFilter !== undefined)
			element.optionsFilter.forEach(
				(value: string | SearchSelectOptionProps) => {
					if (typeof value === "string")
						updatedOptionsFilter.push({
							value: value,
							label: value,
						});
				}
			);
		const searchView =
			element.search !== undefined && element.search.view !== undefined
				? (element.search?.view as { elements: FormElementType[] })
				: undefined;
		const searchColumns =
			element.search !== undefined && element.search.columns !== undefined
				? (element.search.columns as { id: string; label?: string }[])
				: [];
		const deprecatedPayload =
			element.payload !== undefined &&
			typeof element.payload["request-uri"] === "string"
				? {
						...element.payload,
						"request-uri": {
							value: element.payload["request-uri"],
							label: element.payload["request-uri"].slice(
								element.payload["request-uri"].lastIndexOf(
									"/"
								) + 1
							),
						},
						filter: isFilterValid(element.payload.filter)
							? formatFilter(element.payload.filter) || []
							: [],
				  }
				: undefined;
		// @ts-expect-error Select is deprecated so isn't on type
		if (deprecatedPayload?.select !== undefined)
			// @ts-expect-error Select is deprecated so isn't on type
			delete deprecatedPayload.select;

		const newElement: ElementType = {
			elementId: uuidv4(),
			jsonRequired: jsonRequired,
			formValidation: false,
			elementValidation: false,
			...booleanData,
			...element,
			elements:
				element.elements !== undefined
					? addElementData(element.elements, addAlert)
					: undefined,
			id:
				element.id !== undefined && typeof element.id === "string"
					? {
							value: element.id,
							label: element.id.slice(
								element.id.indexOf("/") + 1
							),
					  }
					: undefined,
			resource:
				element.resource !== undefined &&
				typeof element.resource === "string"
					? {
							value: element.resource,
							label: element.resource.slice(
								element.resource.lastIndexOf("/") + 1
							),
					  }
					: undefined,
			link: undefined,
			updateLink:
				element.updateLink && typeof element.updateLink === "string"
					? {
							value: element.updateLink,
							label: element.updateLink.slice(
								element.updateLink.lastIndexOf("/") + 1
							),
					  }
					: element.link && typeof element.link === "string"
					? {
							value: element.link,
							label: element.link.slice(
								element.link.lastIndexOf("/") + 1
							),
					  }
					: undefined,
			createLink:
				element.createLink && typeof element.createLink === "string"
					? {
							value: element.createLink,
							label: element.createLink.slice(
								element.createLink.lastIndexOf("/") + 1
							),
					  }
					: undefined,
			numberFormat:
				element.numberFormat !== undefined &&
				typeof element.numberFormat.rounding === "string"
					? {
							precision: element.numberFormat.precision,
							rounding: {
								value: element.numberFormat.rounding,
								label: _.startCase(
									element.numberFormat.rounding
								),
							},
					  }
					: undefined,
			options:
				element.options !== undefined
					? element.options
					: element.elem === "select"
					? []
					: undefined,
			optionsId:
				element.optionsId !== undefined &&
				typeof element.optionsId === "string"
					? {
							value: element.optionsId,
							label: element.optionsId.slice(
								element.optionsId.lastIndexOf("/") + 1
							),
					  }
					: undefined,
			optionsPath:
				element.optionsPath !== undefined &&
				typeof element.optionsPath === "string"
					? {
							value: element.optionsPath,
							label: element.optionsPath.includes("enum")
								? element.optionsPath.slice(
										element.optionsPath.indexOf(
											"properties/"
										) + 11,
										element.optionsPath.lastIndexOf("/enum")
								  )
								: element.optionsPath,
					  }
					: undefined,
			optionsFilter:
				updatedOptionsFilter.length > 0
					? updatedOptionsFilter
					: undefined,
			searchField:
				element.searchField !== undefined &&
				typeof element.searchField === "string"
					? {
							value: element.searchField,
							label: element.searchField,
					  }
					: undefined,
			populatesFrom:
				element.populatesFrom !== undefined &&
				typeof element.populatesFrom === "string"
					? {
							value: element.populatesFrom,
							label: element.populatesFrom.slice(
								element.populatesFrom.lastIndexOf("/") + 1
							),
					  }
					: undefined,
			populatesKey:
				element.populatesKey !== undefined &&
				typeof element.populatesKey === "string"
					? {
							value: element.populatesKey,
							label: element.populatesKey.slice(
								element.populatesKey.indexOf("/") + 1
							),
					  }
					: undefined,
			payload: undefined,
			search:
				element.search !== undefined &&
				element.search.payload !== undefined &&
				typeof element.search.payload["request-uri"] === "string" &&
				searchColumns !== undefined
					? {
							...element.search,
							payload: {
								...element.search.payload,
								"request-uri": {
									value: element.search.payload[
										"request-uri"
									],
									label: element.search.payload[
										"request-uri"
									].slice(
										element.search.payload[
											"request-uri"
										].lastIndexOf("/") + 1
									),
								},
								filter: isFilterValid(
									element.search.payload.filter
								)
									? formatFilter(
											element.search.payload.filter
									  ) || []
									: [],
							},
							label:
								typeof element.search.label === "string"
									? {
											value: element.search.label,
											label: element.search.label.slice(
												element.search.label.indexOf(
													"/"
												) + 1
											),
									  }
									: undefined,
							overwriteDataWith:
								typeof element.search.overwriteDataWith ===
								"string"
									? {
											value: element.search
												.overwriteDataWith,
											label: element.search.overwriteDataWith.slice(
												element.search.overwriteDataWith.indexOf(
													"/"
												) + 1
											),
									  }
									: undefined,
							columns: searchColumns.map(
								(item: { id: string; label?: string }) => {
									return {
										id: {
											value: item.id,
											label: item.id.slice(
												item.id.indexOf("/") + 1
											),
										},
										label: item.label,
									};
								}
							),
							view:
								element.search.view !== undefined &&
								searchView !== undefined &&
								searchView.elements.length > 0
									? addElementData(
											searchView.elements,
											addAlert
									  )
									: undefined,
					  }
					: deprecatedPayload !== undefined
					? {
							payload: {
								...deprecatedPayload,
							},
							columns: [],
							label:
								element.labelField !== undefined
									? {
											value: `/${element.labelField.replace(
												".",
												"/"
											)}`,
											label: `/${element.labelField.replace(
												".",
												"/"
											)}`,
									  }
									: undefined,
					  }
					: undefined,
			afterPatch:
				element.afterPatch !== undefined
					? {
							...element.afterPatch,
							logic: addEmptyParams(element.afterPatch.logic),
					  }
					: undefined,
		};
		if (
			!newElement.search?.payload?.filter?.length &&
			element.search?.payload?.filter?.AND?.length
		)
			addAlert("unsupportedFilter", status.WARNING);

		if (newElement.elements === undefined) delete newElement.elements;
		if (newElement.id === undefined) delete newElement.id;
		if (newElement.resource === undefined) delete newElement.resource;
		if (newElement.link === undefined) delete newElement.link;
		if (newElement.createLink === undefined) delete newElement.createLink;
		if (newElement.updateLink === undefined) delete newElement.updateLink;
		if (newElement.options === undefined) delete newElement.options;
		if (newElement.optionsId === undefined) delete newElement.optionsId;
		if (newElement.optionsPath === undefined) delete newElement.optionsPath;
		if (newElement.optionsFilter === undefined)
			delete newElement.optionsFilter;
		if (newElement.searchField === undefined) delete newElement.searchField;
		if (newElement.populatesFrom === undefined)
			delete newElement.populatesFrom;
		if (newElement.populatesKey === undefined)
			delete newElement.populatesKey;
		if (newElement.afterPatch === undefined) delete newElement.afterPatch;
		delete newElement.payload;
		if (
			// @ts-expect-error searchFields is deprecated
			newElement.searchFields !== undefined &&
			// @ts-expect-error searchFields is deprecated
			newElement.searchFields !== null
		)
			// @ts-expect-error searchFields is deprecated
			delete newElement.searchFields;
		delete newElement.labelField;
		// @ts-expect-error select is deprecated
		if (newElement.select) delete newElement.select;
		if (newElement.search === undefined) delete newElement.search;
		if (newElement.numberFormat === undefined)
			delete newElement.numberFormat;
		elementsArray.push(newElement);
	});
	return elementsArray;
};
